import { ResizeEntry, ResizeSensor } from "@blueprintjs/core";
import * as React from "react";
import { RecoilRoot } from "recoil";
import styled from "styled-components";

import { Navigator } from "./Navigator";
import { WeekView } from "./WeekView/WeekView";
import { WeekViewHeader } from "./WeekView/WeekViewHeader";
import { ECalendarTlCode } from "./ECalendarTlCode";
import {
  CalendarContextProvider,
  CalendarContextProviderProps,
  useCalendarContext,
} from "./CalendarContext";

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const HeaderBloc = styled.div``;

const ContentBloc = styled.div`
  overflow: auto;
  background-color: white;
  flex: 1;
`;

function InnerCalendar() {
  const { viewMode: calendarMode, setContentOverflow } = useCalendarContext();

  const View = React.useMemo(() => {
    switch (calendarMode) {
      case "week":
        return {
          header: <WeekViewHeader />,
          content: <WeekView />,
        };
      default:
        return {
          header: <div>Header {calendarMode} not implemented</div>,
          content: <div>Content {calendarMode} not implemented</div>,
        };
    }
  }, [calendarMode]);

  const [contentSize, setContentSize] = React.useState<ResizeEntry>();
  const contentBlocRef = React.useRef<HTMLDivElement>();

  React.useEffect(() => {
    const computedOverflow =
      contentSize?.contentRect.height > contentBlocRef.current?.clientHeight;
    setContentOverflow(computedOverflow);
  }, [contentSize?.contentRect.height, setContentOverflow]);

  return (
    <Container>
      <HeaderBloc>
        <Navigator />
        {View.header}
      </HeaderBloc>
      <ContentBloc ref={contentBlocRef}>
        <ResizeSensor
          onResize={(entries) => setContentSize(entries.reverse()[0])}
        >
          {View.content}
        </ResizeSensor>
      </ContentBloc>
    </Container>
  );
}

interface CalendarProps
  extends Omit<CalendarContextProviderProps, "translateFn" | "children"> {
  translateFn?: (code: string) => string;
}
export const Calendar = ({
  ...props
}: React.PropsWithChildren<CalendarProps>) => {
  const { translateFn } = props;

  const finalTranslateFn = React.useCallback(
    (code: ECalendarTlCode) => {
      if (translateFn) {
        return translateFn("" + [ECalendarTlCode[code]]);
      }
      return ECalendarTlCode[code];
    },
    [translateFn]
  );
  return (
    <RecoilRoot>
      <CalendarContextProvider {...props} translateFn={finalTranslateFn}>
        <InnerCalendar />
      </CalendarContextProvider>
    </RecoilRoot>
  );
};
